import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {ProjectId, UserId} from '../../types/ids';
import {UserService} from '../user.service';
import {FeatureService} from '../feature.service';
import {OpenDeviceConsolePermission} from '../../constants/permission.constants';
import {Device} from '../../types/devices/device';
import {Ticket} from '../../types/ticket';

export interface ConsoleAccessibilityParams {
    mayAccessConsole: boolean;
    isAccessedByDifferentUser: boolean;
    exclusiveConsole: boolean;
    isReservedByAnotherUser: boolean;
    hasAnnotations: boolean;
    annotationStatus?: string;
    belongsToDifferentProject?: boolean;
}

/**
 * Centralizes the "can this user open a console for this device" rules, so the various places that
 * gate a console-access button (device summary card, workbench deployed-devices list, ...) share one
 * definition of the rule instead of drifting apart via separate copies.
 */
@Injectable({
    providedIn: 'root'
})
export class ConsoleAccessService {

    constructor(private userService: UserService, private featureService: FeatureService) {
    }

    getOpenConsolePermission$(projectId: ProjectId): Observable<boolean> {
        return this.userService.hasPermissionInProject(projectId, OpenDeviceConsolePermission);
    }

    getExclusiveConsoleFeatureActive$(): Observable<boolean> {
        return this.featureService.getFeature('webmate.console.single-user-only').pipe(map(f => f?.isActive ?? false));
    }

    getReservationsFeatureActive$(): Observable<boolean> {
        return this.featureService.getFeature('webmate.device-slot-reservations').pipe(map(f => f?.isActive ?? false));
    }

    /** Finds the ConsoleAccess ticket belonging to someone other than the given user, if any. */
    findOtherUsersConsoleAccessTicket(device: Device, currentUserId: UserId): Ticket | undefined {
        return device.tickets.find(t => t.job.name === 'ConsoleAccess' && t.userId !== currentUserId);
    }

    /** Finds ALL ConsoleAccess tickets belonging to users other than the given one - there can be more than
     * one when exclusive-console mode is off and several users hold a session on the same device. */
    findOtherUsersConsoleAccessTickets(device: Device, currentUserId: UserId): Ticket[] {
        return device.tickets.filter(t => t.job.name === 'ConsoleAccess' && t.userId !== currentUserId);
    }

    isConsoleAccessible(params: ConsoleAccessibilityParams): boolean {
        return params.mayAccessConsole
            && !params.belongsToDifferentProject
            && !(params.isAccessedByDifferentUser && params.exclusiveConsole)
            && !params.isReservedByAnotherUser
            && (!params.hasAnnotations || params.annotationStatus !== 'update');
    }
}
